/** Quality bitmask. Mirrored in db/001_schema.sql and app/ingest/qc.py.
 *
 *  Quality is never a reason to hide a reading. A value can be both stale and
 *  out of range at once, and thresholds get revised — the UI's job is to say
 *  what was flagged, not to decide what the reader is allowed to see.
 */

export const MISSING = 1
export const OUT_OF_RANGE = 2
export const SPIKE = 4
export const STALE = 8
export const SENTINEL = 16
export const COUNTER_RESET = 32
export const NOT_SCHEDULED = 64

interface Flag {
  bit: number
  name: string
  /** Why it fired, in the words an operator would use. */
  meaning: string
  /** Whether it suggests the reading is wrong, or merely explains it.
   *  COUNTER_RESET on a daily counter at midnight is correct behaviour; a
   *  SPIKE is not. */
  severity: 'suspect' | 'expected'
}

export const FLAGS: Flag[] = [
  { bit: MISSING, name: 'MISSING', severity: 'suspect',
    meaning: 'expected by the poll interval, absent or null' },
  { bit: OUT_OF_RANGE, name: 'OUT_OF_RANGE', severity: 'suspect',
    meaning: 'outside the plausible range for this parameter' },
  { bit: SPIKE, name: 'SPIKE', severity: 'suspect',
    meaning: 'changed faster than the parameter physically can' },
  { bit: STALE, name: 'STALE', severity: 'suspect',
    meaning: 'identical for long enough to suggest a frozen sensor' },
  { bit: SENTINEL, name: 'SENTINEL', severity: 'expected',
    meaning: 'known placeholder: the vane reports 0 while wind speed is 0' },
  { bit: COUNTER_RESET, name: 'COUNTER_RESET', severity: 'expected',
    meaning: 'a daily counter rolled over at local midnight' },
  { bit: NOT_SCHEDULED, name: 'NOT_SCHEDULED', severity: 'expected',
    meaning: 'event-driven channel: its absence is expected, not missing' },
]

export function describe(quality: number): Flag[] {
  return FLAGS.filter((flag) => (quality & flag.bit) !== 0)
}

export function names(quality: number): string[] {
  return describe(quality).map((flag) => flag.name)
}

/** Only the flags that call the value into question.
 *
 *  Counting COUNTER_RESET as suspect would mark every daily counter at
 *  midnight and every Shaori precipitation reading, which trains the reader
 *  to ignore the indicator entirely.
 */
export function isSuspect(quality: number): boolean {
  return describe(quality).some((flag) => flag.severity === 'suspect')
}
